export const VERIFICATION_STATUSES = [
  'draft',
  'submitted',
  'in_review',
  'passed',
  'failed',
  'expired',
  'revoked',
] as const;

export type VerificationStatus = (typeof VERIFICATION_STATUSES)[number];
export type VerificationStatusFilter = VerificationStatus | 'all';

/** What is being verified. `both` types can apply to more than one subject. */
export type VerificationScope = 'user' | 'org' | 'listing';

export interface VerificationSubject {
  kind: 'user' | 'organization' | 'listing' | null;
  id: number | null;
  label: string | null;
}

export interface Verification {
  vid: number;
  status: VerificationStatus;
  /** `name_system` of the type, e.g. `identity_gh_nia`. Joined by the API. */
  type_name: string | null;
  /** The type's own scope: `user`, `org` or `both`. */
  type_scope: string | null;
  subject: VerificationSubject;
  vtypid: number | null;
  uid: number | null;
  oid: number | null;
  eid: number | null;
  passed: number | null;
  failed: number | null;
  review: number | null;
  identity: number | null;
  address: number | null;
  notes: string | null;
  date_added: string | null;
  date_modified: string | null;
  date_expired: string | null;
}

export interface VerificationType {
  vtypid: number;
  name_system: string;
  scope: string;
  active: number;
}

export interface VerificationsParams {
  status?: VerificationStatusFilter;
  scope?: VerificationScope | '';
  type?: string;
  q?: string;
  page?: number;
  page_size?: number;
}

export interface VerificationsResponse {
  verifications: Verification[];
  total: number;
  page: number;
  page_size: number;
  counts: Record<VerificationStatus, number>;
}

/** Revoke requires a reason; approve and reject take no body. */
export type VerificationAction = 'approve' | 'reject' | 'revoke';

/** A checklist item is the unit a reviewer actually decides on. */
export type ChecklistItemStatus = 'pending' | 'submitted' | 'approved' | 'rejected' | 'not_applicable';

export interface ChecklistItem {
  vcid: number;
  vid: number;
  item_key: string;
  label: string;
  item_type: string;
  status: ChecklistItemStatus;
  required: number | boolean;
  sort_order: number;
  asid_json: number[] | null;
  value_json: Record<string, unknown> | null;
  config_json: Record<string, unknown> | null;
  reviewer_uid: number | null;
  reviewer_notes: string | null;
  date_submitted: string | null;
  date_reviewed: string | null;
}

export interface VerificationStatsDay {
  day: string;
  [status: string]: string | number;
}

export interface VerificationTypeStat {
  type: string;
  scope: string | null;
  total: number;
  [status: string]: string | number | null;
}

export interface VerificationStats {
  days: number;
  series: VerificationStatsDay[];
  by_type: VerificationTypeStat[];
  /** How long the oldest unreviewed submission has waited. Null when none. */
  oldest_waiting_hours: number | null;
}

export type VerificationLifecycleAction = 'approve' | 'reject' | 'revoke' | 'claim' | 'expire';
